import { LoaderCircle } from "lucide-react";
import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { portalApi } from "./api/client";
import type { ApiEnvelope, TenantInfo } from "./api/types";
import { AuthProvider } from "./auth/AuthContext";
import { AuthShell } from "./components/AuthShell";
import {
  PortalAdminRoute,
  PortalLayout,
  ProtectedRoute,
} from "./layout/PortalLayout";
import { DashboardPage } from "./pages/DashboardPage";
import { DocumentationPage } from "./pages/DocumentationPage";
import { AssetsPage } from "./pages/AssetsPage";
import { BatchReviewPage } from "./pages/BatchReviewPage";
import { getTenantContext } from "./lib/tenant";
import { InvitePage } from "./pages/InvitePage";
import { LoginPage } from "./pages/LoginPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { SelectSupplierPage } from "./pages/SelectSupplierPage";
import { UsersPage } from "./pages/UsersPage";
import { ModuleThemeProvider } from "../../src/components/platform/contexts/ModuleThemeContext";
import { useAuth } from './auth/AuthContext';
import { PrivacyConsentModal } from './components/PrivacyConsentModal';

function PrivacyOverlay({ tenant }: { tenant: TenantInfo }) { const auth = useAuth(); return auth.privacyConsent ? <PrivacyConsentModal tenant={tenant} pending={auth.privacyConsent} onAccept={auth.acceptPrivacy} onDecline={auth.declinePrivacy}/> : null; }

type TenantState =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "ready"; tenant: TenantInfo };

export function App() {
  const tenantContext = React.useMemo(() => getTenantContext(), []);
  const [state, setState] = React.useState<TenantState>(
    tenantContext ? { status: "loading" } : { status: "not-found" }
  );

  React.useEffect(() => {
    if (!tenantContext) return;
    let active = true;

    portalApi
      .get<ApiEnvelope<TenantInfo>>(
        `/platform/portal/${encodeURIComponent(
          tenantContext.slug
        )}/tenant-info`,
        { skipAuth: true, skipRefresh: true } as never
      )
      .then(({ data }) => {
        if (active && data.success && data.data) {
          document.title = `${data.data.nmtenant} | Portal do fornecedor`;
          setState({ status: "ready", tenant: data.data });
        }
      })
      .catch(() => {
        if (active) setState({ status: "not-found" });
      });

    return () => {
      active = false;
    };
  }, [tenantContext]);

  let content: React.ReactNode;

  if (state.status === "loading") {
    content = (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-600">
        <LoaderCircle className="mr-2 animate-spin" size={19} />
        Localizando o portal…
      </main>
    );
  } else if (state.status === "not-found") {
    const fallback: TenantInfo = {
      nmtenant: "Owlen",
      hasportallogo: false,
    };
    content = (
      <AuthShell
        tenant={fallback}
        title="Portal não encontrado"
        description="Verifique o endereço recebido no convite ou entre em contato com a empresa que convidou você."
      >
        <p className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
          Este endereço não está associado a um portal ativo.
        </p>
      </AuthShell>
    );
  } else {
    content = (
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage tenant={state.tenant} />} />
            <Route
              path="/select-supplier"
              element={<SelectSupplierPage tenant={state.tenant} />}
            />
            <Route
              path="/invite/:token"
              element={<InvitePage tenant={state.tenant} />}
            />
            <Route
              path="/reset/:token"
              element={<ResetPasswordPage tenant={state.tenant} />}
            />
            <Route element={<ProtectedRoute />}>
              <Route element={<PortalLayout tenant={state.tenant} />}>
                <Route index element={<DashboardPage />} />
                <Route path="documentation" element={<DocumentationPage />} />
                <Route element={<PortalAdminRoute />}>
                  <Route path="users" element={<UsersPage />} />
                </Route>
                <Route path="assets/:cdassettype" element={<AssetsPage />} />
                <Route
                  path="assets/:cdassettype/batches/:cdbatch"
                  element={<BatchReviewPage />}
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PrivacyOverlay tenant={state.tenant}/>
        </AuthProvider>
      </BrowserRouter>
    );
  }

  return (
    <ModuleThemeProvider module="supplier">
      <div className="min-h-screen" data-module="supplier">
        {content}
      </div>
    </ModuleThemeProvider>
  );
}
